import type { TemplateReference } from "~/common/videoTemplate/types"
import { useState } from "react"
import { Button, Flex, Input, Typography } from "antd"
import { PlusOutlined, UploadOutlined } from "@ant-design/icons"
import { ResourceUploadField } from "~/components/ResourceUploadField"
import { isTemplateMediaUrl } from "~/common/videoTemplate/config"
import styles from "./index.css"
import { createRandomId } from "~/common/utils"

export const MediaField = ({
  value,
  onChange,
  type,
}: {
  value: string
  onChange: (value: string) => void
  type: "image" | "video"
}) => {
  const [uploadOpen, setUploadOpen] = useState(false)
  return (
    <Flex
      vertical
      gap={8}
    >
      <Flex gap={8}>
        <Input
          aria-label={type === "video" ? "视频链接" : "图片链接"}
          value={value}
          placeholder="粘贴完整链接，或上传文件"
          onChange={(event) => onChange(event.target.value)}
        />
        <Button
          aria-label={type === "video" ? "上传视频" : "上传图片"}
          icon={<UploadOutlined />}
          onClick={() => setUploadOpen((open) => !open)}
        />
      </Flex>
      {uploadOpen && (
        <ResourceUploadField
          value={value}
          resourceType={type}
          maxCount={1}
          previewSize="small"
          enforceVideoDurationLimit={false}
          onChange={(next) => {
            onChange(next)
            setUploadOpen(false)
          }}
        />
      )}
    </Flex>
  )
}

export const ReferenceMediaEditor = ({
  value,
  onChange,
  resourceType,
}: {
  resourceType: "image" | "audio"
  value: TemplateReference[]
  onChange: (resources: TemplateReference[]) => void
}) => {
  const label = resourceType === "image" ? "图片" : "音频"
  const [url, setUrl] = useState("")
  const [error, setError] = useState("")
  const updateUrls = (text: string) => {
    const urls = text
      .split("\n")
      .map((item) => item.trim())
      .filter(Boolean)
    onChange(
      urls.map(
        (next, index) =>
          value.find((resource) => resource.url === next) || {
            id: createRandomId(),
            url: next,
            name: `参考${label} ${index + 1}`,
          },
      ),
    )
  }
  const addUrl = () => {
    const next = url.trim()
    if (!isTemplateMediaUrl(next)) {
      setError(`请输入完整的 HTTP 或 HTTPS ${label}链接`)
      return
    }
    if (value.some((resource) => resource.url === next)) {
      setError(`该${label}已添加`)
      return
    }
    updateUrls([...value.map((resource) => resource.url), next].join("\n"))
    setUrl("")
    setError("")
  }
  return (
    <Flex
      vertical
      gap={12}
    >
      <Flex gap={8}>
        <Input
          aria-label={`参考${label}链接`}
          placeholder={`粘贴参考${label}链接`}
          value={url}
          onChange={(event) => setUrl(event.target.value)}
          onPressEnter={addUrl}
        />
        <Button
          icon={<PlusOutlined />}
          onClick={addUrl}
        >
          添加
        </Button>
      </Flex>
      {error && (
        <Typography.Text
          type="danger"
          role="alert"
        >
          {error}
        </Typography.Text>
      )}
      <ResourceUploadField
        resourceType={resourceType}
        value={value.map((resource) => resource.url).join("\n")}
        onChange={updateUrls}
        multiple
        sortable
        previewSize="small"
        previewLabels={Object.fromEntries(
          value.map((resource, index) => [
            resource.url,
            resource.name.trim() || `${label}${index + 1}`,
          ]),
        )}
        showWhiteBgAction={false}
      />
      {value.map((resource, index) => (
        <Flex
          key={resource.id}
          align="center"
          gap={8}
        >
          <Typography.Text className={styles.imageIndex}>
            {label}
            {index + 1}
          </Typography.Text>
          <Input
            aria-label={`${label}${index + 1}名称`}
            placeholder={`${label}名称（选填）`}
            value={resource.name}
            onChange={(event) =>
              onChange(
                value.map((item) =>
                  item.id === resource.id
                    ? { ...item, name: event.target.value }
                    : item,
                ),
              )
            }
          />
        </Flex>
      ))}
    </Flex>
  )
}
